/**
 * One card per rule the register holds, and one dashed card per rule it does not.
 *
 * Naming what the register does not have is the only way the list reads as complete rather than
 * as whatever happened to be found.
 *
 * Every value carries its provenance: the citations with their roles and their server-computed
 * official/unofficial pill, the quoted words, the verification state and the research status. A
 * user reading a computed expiry date should be able to see which instrument it came from, who
 * confirmed it and when it was last checked.
 */

import { Chip } from '~/ui/Pill';
import { CardButton } from '~/ui/Surface';
import { SourceStatusPill, VerificationPill, ResearchStatusBadge } from '../jurisdictions/pills';
import { WithheldBlock } from './Withheld';
import { describeReason } from './reasons';
import { describeVersion, governingVersionOf, liveVersionOf, resultLabelFor, type Vocabulary } from './describe';
import { fmtDate, fmtYearRange, plural } from '~/lib/format';
import { routes, type DateParams } from '~/lib/routes';
import type { AbsentRule, Citation, RuleForFiling, RuleVersion } from '~/api/schema';
import styles from './Matter.module.css';

const CITATION_ROLE: Record<string, string> = {
  primary: 'Primary',
  consolidating: 'Consolidating',
  amending: 'Amending',
  commentary: 'Commentary',
  practice_note: 'Practice note',
};

export interface RuleCardProps {
  code: string;
  matter: string;
  rule: RuleForFiling;
  vocab: Vocabulary;
  dates: DateParams;
  /**
   * What this rule produced once the other supplied dates were taken into account. The panel read
   * takes only the filing date, so a deadline counted from registration is refused there until a
   * calculation has run.
   */
  computed?: { date: string | null; reason: string | null } | undefined;
}

export function RuleCard({ code, matter, rule, vocab, dates, computed }: RuleCardProps) {
  const governing = governingVersionOf(rule);
  const live = liveVersionOf(rule);
  const withheld = governing?.contradiction ?? null;

  const result = computed?.date ?? rule.result;
  const reason = computed?.date ? null : (computed?.reason ?? rule.reason);

  return (
    <div>
      <CardButton
        to={routes.ruleEditor(code, matter, rule.lane, dates)}
        aria-label={`Edit ${rule.lane}`}
        actionLabel="Edit"
        trailing={
          result ? (
            <span className={styles.result}>
              <span className={styles.resultLabel}>{resultLabelFor(rule.shape)}</span>
              {fmtDate(result)}
            </span>
          ) : (
            <span className={styles.resultNone}>
              {describeReason(reason, vocab) ?? 'No date for this filing'}
            </span>
          )
        }
      >
        <div className={styles.ruleHead}>
          <h3>{rule.lane}</h3>
          <span className={styles.spacer} />
        </div>

        {governing ? (
          <p className={styles.calc}>{describeVersion(governing, rule.shape, vocab)}</p>
        ) : (
          <p className={styles.calc}>
            No version of this rule governs the filing date on screen.
          </p>
        )}

        <p className={styles.adoption}>
          {rule.adopted ? `Adopted ${fmtDate(rule.adopted)}` : 'Held since the register opened'}
          {rule.repealed ? ` · repealed ${fmtDate(rule.repealed)}` : ''}
          {rule.custom ? ' · not in the shared catalogue' : ''}
        </p>

        <div className={styles.versions}>
          <span className={styles.versionsLabel}>{plural(rule.versions.length, 'version')}</span>
          {rule.versions.map((version) => (
            <VersionChip
              key={version.id}
              version={version}
              shape={rule.shape}
              vocab={vocab}
              governing={governing?.id === version.id}
              live={live?.id === version.id}
            />
          ))}
        </div>

        {governing ? <Provenance version={governing} /> : null}
      </CardButton>

      {withheld ? (
        <WithheldBlock
          code={code}
          matter={matter}
          lane={rule.lane}
          versionId={governing?.id ?? null}
          contradiction={withheld}
          dateParams={dates as Record<string, string | undefined>}
        />
      ) : null}
    </div>
  );
}

/**
 * Two emphasis states overlay and must stay separable: `governing` is the version that decides
 * the filing on screen, `live` is the one in force today. They are frequently the same chip and
 * frequently not, and that distinction is the whole point of the filing-date control.
 *
 * The arithmetic goes in visually-hidden text, not in `title`, which no keyboard or screen-reader
 * user can reach.
 */
function VersionChip({
  version,
  shape,
  vocab,
  governing,
  live,
}: {
  version: RuleVersion;
  shape: string;
  vocab: Vocabulary;
  governing: boolean;
  live: boolean;
}) {
  return (
    <Chip emphasis={governing ? 'strong' : 'default'} outlined={live}>
      v{version.version} · {fmtYearRange(version.from, version.to)}
      <span className="visually-hidden">
        {` — ${describeVersion(version, shape, vocab)}`}
        {governing ? ' Governs the filing on screen.' : ''}
        {live ? ' In force today.' : ''}
        {version.inherited && version.to ? ' Its end date is inherited, not recorded.' : ''}
        {version.contradiction ? ' Contradicted and withheld from every calculation.' : ''}
      </span>
    </Chip>
  );
}

function Provenance({ version }: { version: RuleVersion }) {
  if (
    (version.citations ?? []).length === 0 &&
    !version.instrumentTitle &&
    !version.quote &&
    !version.lastVerifiedOn
  ) {
    return (
      <div className={styles.provenance}>
        <span>No instrument is recorded behind this version yet.</span>
      </div>
    );
  }

  return (
    <div className={styles.provenance}>
      {version.instrumentTitle ? (
        <span>
          {version.instrumentTitle}
          {version.instrumentProvision ? `, ${version.instrumentProvision}` : ''}
        </span>
      ) : null}
      {version.quote ? <span className={styles.citationQuote}>&ldquo;{version.quote}&rdquo;</span> : null}
      {(version.citations ?? []).map((citation) => (
        <CitationRow key={`${citation.url ?? citation.citationText}-${citation.ordinal}`} citation={citation} />
      ))}
      <span className={styles.citationRow}>
        <VerificationPill state={version.verificationState} />
        <ResearchStatusBadge status={version.researchStatus} />
        {version.lastVerifiedOn ? <span>Last confirmed {fmtDate(version.lastVerifiedOn)}</span> : null}
        <span>Recorded as {version.provenance.replace('_', ' ')}</span>
      </span>
    </div>
  );
}

function CitationRow({ citation }: { citation: Citation }) {
  return (
    <span className={styles.citationRow}>
      <span>{CITATION_ROLE[citation.role] ?? citation.role}</span>
      <SourceStatusPill status={citation.sourceStatus} />
      {citation.url ? (
        <a
          href={citation.url}
          target="_blank"
          rel="noreferrer noopener"
          className={styles.externalLink}
        >
          {citation.url}
        </a>
      ) : (
        <span>{citation.citationText ?? citation.physicalLocation ?? 'No address recorded'}</span>
      )}
      {citation.quote ? <span className={styles.citationQuote}>&ldquo;{citation.quote}&rdquo;</span> : null}
    </span>
  );
}

export function AbsentRuleCard({
  code,
  matter,
  rule,
  dates,
}: {
  code: string;
  matter: string;
  rule: AbsentRule;
  dates: DateParams;
}) {
  return (
    <CardButton
      to={routes.ruleEditor(code, matter, rule.lane, dates)}
      aria-label={`Adopt ${rule.lane}`}
      actionLabel="Adopt"
      muted
      trailing={<span className={styles.resultNone}>Not adopted</span>}
    >
      <div className={styles.ruleHead}>
        <h3>{rule.lane}</h3>
        <span className={styles.spacer} />
      </div>
      {rule.note ? <p className={styles.calc}>{rule.note}</p> : null}
    </CardButton>
  );
}
